<template>
  <UITooltip>
    <template #trigger>
      <div>
        <UIDropdownTree
          :options="options"
          trigger-label=""
          placement="right-start"
          @onSelect="appendValueAtCursor"
        >
          <template #trigger>
            <UIButton
              id="dd"
              quaternary
              class="px-1"
              :aria-label="$t('common.customValues')"
              :title="$t('common.customValues')"
            >
              <Tag01Icon class="h-5 w-5" />
            </UIButton>
          </template>
        </UIDropdownTree>
      </div>
    </template>
    {{ $t("common.customValues") }}
  </UITooltip>
</template>

<script setup lang="ts">
// @ts-ignore - icon types available at runtime
import { Tag01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIDropdownTree, UITooltip } from "@gohighlevel/ghl-ui";
// @ts-ignore - editor types available at runtime
import { useAppStore } from "@/store/app";
import { Editor } from "@tiptap/vue-3";
import { onMounted, ref } from "vue";
// @ts-ignore - local types
import { CONTACT_CHILDREN, RIGHT_NOW_CHILDREN } from "./constants";

type Props = { editor: Editor; isSubmitMessage?: boolean };
const props = defineProps<Props>();

type TreeOption = { label: string; value: string; children?: TreeOption[] };

const options = ref<TreeOption[]>([]);
const appStore = useAppStore();

const mapCustomFieldsToTree = (fields: any[] = []): TreeOption => {
  // Filter only custom fields that belong to the contact model
  const contactFields = (fields || []).filter((field: any) => {
    const model = (field?.model ?? "contact").toString().toLowerCase();
    return model === "contact";
  });
  const children: TreeOption[] = contactFields.map((field: any) => {
    const key: string = field?.fieldKey || field?.uniqueKey || field?.key || "";
    const label: string = field?.name || field?.label || key;
    const sanitizedKey = typeof key === "string" ? key.trim() : "";
    const value = sanitizedKey ? `{{${sanitizedKey}}}` : "";
    return { label, value } as TreeOption;
  });
  return {
    label: "Custom Fields",
    value: "custom_fields",
    children,
  } as TreeOption;
};

const buildStaticGroups = (): TreeOption[] => {
  const groups: TreeOption[] = [];
  if (props.isSubmitMessage) {
    const customFieldChildren =
      mapCustomFieldsToTree(((appStore as any).customFields || []) as any[]).children || [];
    const contactChildren: TreeOption[] = [
      ...CONTACT_CHILDREN,
      { label: "Custom Fields", value: "custom_fields", children: customFieldChildren },
    ];
    groups.push({ label: "Contact", value: "contact", children: contactChildren });
  }
  groups.push({ label: "Right now", value: "right_now", children: RIGHT_NOW_CHILDREN });
  return groups;
};

const mapCustomValuesToTree = (list: any[] = []): TreeOption | null => {
  if (!list.length) {
    return null;
  }
  const mapNode = (node: any): TreeOption => {
    if (node?.children?.length) {
      return {
        label: node.label || node.name,
        value: node.value || node.name,
        children: node.children.map(mapNode),
      };
    }
    const leafLabel: string = node?.label || node?.name || node?.value || "";
    const rawValue: string = (node?.fieldKey || node?.value || "").toString().trim();
    let safeValue = rawValue;
    if (/^\{\{.*\}\}$/.test(rawValue)) {
      safeValue = rawValue.replace(/^\{\{\s*/, "{{").replace(/\s*\}\}$/, "}}");
    } else if (rawValue) {
      safeValue = `{{${rawValue}}}`;
    }
    return { label: leafLabel, value: safeValue } as TreeOption;
  };
  const children: TreeOption[] = list.map(mapNode);
  return { label: "Custom Values", value: "custom_values", children } as TreeOption;
};

onMounted(async () => {
  try {
    if (!(appStore as any).customFields?.length && props.isSubmitMessage) {
      await (appStore as any).getCustomFields("", 0, 5000, "field", "", true, "all");
    }
  } catch (_) {}
  try {
    if (!(appStore as any).customValues?.length) {
      await (appStore as any).getCustomValues();
    }
  } catch (_) {}

  const result: TreeOption[] = [];
  buildStaticGroups().forEach((g) => result.push(g));
  const customValuesGroup = mapCustomValuesToTree((appStore as any).customValues || []);
  if (customValuesGroup && customValuesGroup.children && customValuesGroup.children.length) {
    result.push(customValuesGroup);
  }
  options.value = result;
});

const appendValueAtCursor = (selected: string | TreeOption) => {
  const value = typeof selected === "string" ? selected : selected?.value;
  if (!value) {
    return;
  }
  props.editor.chain().focus().insertContent(value).run();
};
</script>
